/**
 * TierInterestSection — Admin "Save my spot" viewer.
 *
 * Read-only v1 (Feb 2026 dispatch). Read-only: no delete/edit/ack/bulk.
 * Mounts inside `TiersAndPlansAdminPage` (natural home per dispatch —
 * "Location: Admin portal, 'Tiers & plans' section").
 *
 * Data-model note (mirrored in the backend module docstring):
 *   * `POST /api/partner/tier/select` writes ONLY the partner's user
 *     doc via `$set`, so multi-capture-per-partner is IMPOSSIBLE
 *     under v1 — dedupe-latest is inherent.
 *   * `captures_count` therefore always equals 1 in v1. Displayed
 *     for schema stability; a future history-collection dispatch
 *     could raise it without changing the response shape.
 *   * `current_tier` is resolved LIVE from `users.partner_tier` at
 *     read time. If a partner upgraded between capture and admin
 *     view, this column shows the CURRENT value. The column
 *     header carries a tooltip flagging this.
 *
 * No inline hex colors (Family-L invariant) — Tailwind tokens only.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Download, Users, TrendingUp, Info } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}


function TierBadge({ tier }) {
  // Discrete visual affordance for tier — pill via Tailwind tokens
  // only (no inline hex; classes resolve to pp-* palette in tailwind.config).
  const cls =
    tier === "elite"
      ? "bg-pp-warning/20 text-pp-warning"
      : tier === "preferred"
        ? "bg-pp-orange/20 text-pp-orange"
        : "bg-pp-border text-pp-muted";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}
      data-testid={`tier-interest-tier-badge-${tier}`}
    >
      {tier}
    </span>
  );
}


function SummaryTile({ label, value, testid, icon: Icon }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-pp-border bg-pp-surface px-4 py-3"
      data-testid={testid}
    >
      {Icon && <Icon className="h-5 w-5 text-pp-orange" aria-hidden="true" />}
      <div>
        <div className="text-xs uppercase tracking-wide text-pp-muted">{label}</div>
        <div className="text-2xl font-semibold text-pp-text">{value}</div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Main section
// ---------------------------------------------------------------------------
export default function TierInterestSection() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    total_interested_partners: 0,
    preferred_interested_partners: 0,
    elite_interested_partners: 0,
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tier-interest", { params: { limit: 500 } });
      setItems(data.items || []);
      setSummary(data.summary || {
        total_interested_partners: 0,
        preferred_interested_partners: 0,
        elite_interested_partners: 0,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load tier interest.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/admin/tier-interest/export.csv", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `tier_interest_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Tier interest CSV downloaded.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "CSV export failed.");
    } finally {
      setExporting(false);
    }
  };

  const emptyMsg = useMemo(() => {
    if (loading) return null;
    if (items.length === 0) return "No tier interest captured yet.";
    return null;
  }, [loading, items]);

  return (
    <TooltipProvider delayDuration={200}>
      <section
        className="space-y-4"
        data-testid="admin-tier-interest-section"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-xl font-semibold text-pp-text">Tier interest</h3>
            <p className="text-sm text-pp-muted mt-1 max-w-xl">
              Partners who clicked <span className="font-medium">Save my spot</span> on the
              Pluckonomics page. Ordered newest-first. Read-only surface — reach out via
              the export for billing-launch email.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="tier-interest-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              disabled={exporting || items.length === 0}
              data-testid="tier-interest-export-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary tiles — distinct-partner counts */}
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          data-testid="tier-interest-summary-tiles"
        >
          <SummaryTile
            label="Total interested"
            value={summary.total_interested_partners}
            testid="tier-interest-tile-total"
            icon={Users}
          />
          <SummaryTile
            label="Preferred interest"
            value={summary.preferred_interested_partners}
            testid="tier-interest-tile-preferred"
            icon={TrendingUp}
          />
          <SummaryTile
            label="Elite interest"
            value={summary.elite_interested_partners}
            testid="tier-interest-tile-elite"
            icon={TrendingUp}
          />
        </div>

        {/* Table */}
        <div
          className="rounded-lg border border-pp-border bg-pp-surface overflow-x-auto"
          data-testid="tier-interest-table-wrap"
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>
                  <div className="inline-flex items-center gap-1">
                    Current tier
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="cursor-help"
                          data-testid="tier-interest-current-tier-tooltip-trigger"
                        >
                          <Info className="h-3 w-3 text-pp-muted" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <span className="text-xs">
                          Resolved live from the partner's account. If they upgraded
                          since capture, this shows the CURRENT tier — not the tier
                          they had when they clicked Save my spot.
                        </span>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableHead>
                <TableHead>Requested tier</TableHead>
                <TableHead>Captured</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center py-8 text-pp-muted"
                    data-testid="tier-interest-empty"
                  >
                    {emptyMsg}
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((row) => (
                <TableRow
                  key={row.partner_id}
                  data-testid={`tier-interest-row-${row.partner_id}`}
                >
                  <TableCell className="font-medium">{row.partner_name}</TableCell>
                  <TableCell>
                    <TierBadge tier={row.current_tier} />
                  </TableCell>
                  <TableCell>
                    <TierBadge tier={row.requested_tier} />
                  </TableCell>
                  <TableCell>{formatDateTime(row.captured_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </TooltipProvider>
  );
}
